import type { Recording } from "@radar-sim/engine";

/**
 * Recording persistence layer. Saves captured Recordings to
 * IndexedDB so they survive page reload — the simulator was
 * previously memory-only and a refresh dropped any saved debriefs.
 *
 * Schema (single object store "recordings"):
 *   key: string (recording.metadata.id)
 *   value: SavedRecording { id, scenarioId, scenarioName, label,
 *                            capturedAt, snapshotCount, recording }
 *
 * The lightweight metadata fields are duplicated outside the nested
 * Recording so the list view doesn't have to deserialise full
 * snapshots just to render a row. The full Recording is fetched
 * lazily when the user opens it in debrief.
 */

const DB_NAME = "radar-sim-recordings";
const DB_VERSION = 1;
const STORE = "recordings";

export interface SavedRecording {
  id: string;
  scenarioId: string;
  scenarioName: string;
  label?: string;
  capturedAt: string; // ISO datetime
  snapshotCount: number;
  recording: Recording;
}

export interface SavedRecordingMetadata {
  id: string;
  scenarioId: string;
  scenarioName: string;
  label?: string;
  capturedAt: string;
  snapshotCount: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("by-captured", "capturedAt");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveRecording(
  recording: Recording,
  scenarioName: string,
): Promise<SavedRecording> {
  const entry: SavedRecording = {
    id: recording.metadata.id,
    scenarioId: recording.metadata.scenarioId,
    scenarioName,
    capturedAt: recording.metadata.capturedAt,
    snapshotCount: recording.snapshots.length,
    recording,
    ...(recording.metadata.label !== undefined
      ? { label: recording.metadata.label }
      : {}),
  };
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).put(entry);
  });
  db.close();
  return entry;
}

/** List saved-recording metadata (no full snapshot streams) sorted
 *  by captured-at descending. Cheap; suitable for a list UI. */
export async function listRecordings(): Promise<
  ReadonlyArray<SavedRecordingMetadata>
> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const all = (req.result as SavedRecording[]).map((r) => {
        const out: SavedRecordingMetadata = {
          id: r.id,
          scenarioId: r.scenarioId,
          scenarioName: r.scenarioName,
          capturedAt: r.capturedAt,
          snapshotCount: r.snapshotCount,
        };
        if (r.label !== undefined) out.label = r.label;
        return out;
      });
      all.sort((a, b) => (a.capturedAt < b.capturedAt ? 1 : -1));
      db.close();
      resolve(all);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

/** Hydrate the full Recording for a saved id. Used when the user
 *  opens an entry from the list. Returns undefined if the id isn't
 *  present (e.g. it was deleted in another tab). */
export async function loadRecording(id: string): Promise<Recording | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => {
      const entry = req.result as SavedRecording | undefined;
      db.close();
      resolve(entry?.recording);
    };
    req.onerror = () => {
      db.close();
      reject(req.error);
    };
  });
}

export async function deleteRecording(id: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).delete(id);
  });
  db.close();
}
